import React, { useEffect, useState } from "react";
import { styled } from "styled-components";

import EastIcon from "@mui/icons-material/East";
import { useNavigate } from "react-router-dom";

import Slider from "react-slick";
import AutoSlideImage from "./AutoSlideImage";
import { fetchProjects, getCachedProjects } from "../../axios/projects";

const getProjectName = (project) => {
  const title = project?.title?.trim();

  if (title) {
    return title.split(/\s+for\s+/i)[1]?.trim() || title;
  }

  return project?.project_name?.trim() || "Untitled project";
};

function CustomProjectSlider() {
  const [projectsData, setProjectsData] = useState(
    () => getCachedProjects("residential") || []
  );
  const [width, setWidth] = useState(window.innerWidth);
  const navigate = useNavigate();

  useEffect(() => {
    const controller = new AbortController();
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);

    fetchProjects("residential", { signal: controller.signal })
      .then(setProjectsData)
      .catch((error) => {
        if (!controller.signal.aborted) {
          console.error("Unable to refresh homepage projects:", error);
        }
      });

    return () => {
      controller.abort();
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const settings = {
    arrows: false,
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    cssEase: "ease",
  };

  const openProject = (slug) => navigate(`/project/${slug}`);

  const handleProjectKeyDown = (event, slug) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openProject(slug);
    }
  };

  return (
    <Section>
      <Container>
        <BreadCrumb>Projects /</BreadCrumb>

        {width < 580 ? (
          <Slider {...settings} className="mobileSlider">
            {projectsData.map((project, index) => (
              <MobSlider key={project.id || index} className="innerArea">
                <Wrapper>
                  <ProjectHighlight>
                    <AutoSlideImage assets={project?.project_images} />
                  </ProjectHighlight>

                  <SideBar>
                    <SmallPreview>
                      <img
                        loading="lazy"
                        src={project?.project_images?.[1]?.resized_image}
                        alt=""
                      />
                    </SmallPreview>

                    <SmallPreview>
                      <img
                        loading="lazy"
                        src={project?.project_images?.[2]?.resized_image}
                        alt=""
                      />
                    </SmallPreview>

                    <SmallPreview last={1}>
                      <img
                        loading="lazy"
                        src={project?.project_images?.[3]?.resized_image}
                        alt=""
                      />
                      <p onClick={() => openProject(project.slug)}>
                        View <br /> More
                      </p>
                    </SmallPreview>
                  </SideBar>
                </Wrapper>

                <ClientInfo>
                  <p>{project?.title || project?.project_name}</p>
                </ClientInfo>
              </MobSlider>
            ))}
          </Slider>
        ) : (
          <>
            <SliderArea>
              {projectsData.slice(0, 6).map((project, index) => (
                <OneProject
                  key={project.id || index}
                  className={
                    ["one", "two", "three", "four", "five", "six"][index]
                  }
                  role="link"
                  tabIndex={0}
                  aria-label={`View ${getProjectName(project)}`}
                  onClick={() => openProject(project.slug)}
                  onKeyDown={(event) => handleProjectKeyDown(event, project.slug)}
                >
                  {index % 2 === 0 ? (
                    <>
                      <ImgDiv
                        img={project?.project_images?.[0]?.image || ""}
                        role="img"
                        aria-label={getProjectName(project)}
                      />
                      <Title>{getProjectName(project)}</Title>
                    </>
                  ) : (
                    <>
                      <Title>{getProjectName(project)}</Title>
                      <ImgDiv
                        img={project?.project_images?.[0]?.image || ""}
                        role="img"
                        aria-label={getProjectName(project)}
                      />
                    </>
                  )}
                </OneProject>
              ))}
            </SliderArea>

            <ViewMore>
              <p onClick={() => navigate("/projects/residential")}>
                View More <EastIcon />
              </p>
            </ViewMore>
          </>
        )}
      </Container>
    </Section>
  );
}

export default CustomProjectSlider;

const Section = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 25px 0;
`;

const Container = styled.div`
  width: 87%;
  display: flex;
  flex-direction: column;

  @media (max-width: 1440px) {
    width: 93%;
  }

  @media (max-width: 1875px) {
    .five,
    .six {
      display: none;
    }
  }

  @media (max-width: 1325px) {
    .five,
    .six,
    .four {
      display: none;
    }
  }

  @media (max-width: 968px) {
    width: 95%;
  }
`;

const ImgDiv = styled.div`
  width: 100%;
  height: 100%;
  ${(props) =>
    props.img && `background: url(${props.img}) 50% 50% / cover no-repeat;`}
  filter: blur(0);
  opacity: 1;
  transition:
    filter 350ms ease,
    opacity 350ms ease,
    background-size 550ms cubic-bezier(0.22, 1, 0.36, 1);
`;

const OneProject = styled.div`
  display: flex;
  flex-direction: column;
  height: 475px;
  width: 350px;
  cursor: pointer;
  transition: transform 350ms cubic-bezier(0.22, 1, 0.36, 1);

  &:hover,
  &:focus-visible {
    transform: translateY(-6px);
    outline: none;
  }

  &:hover ${ImgDiv},
  &:focus-visible ${ImgDiv} {
    background-size: 106%;
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px var(--primary-cl);
  }
`;

const SliderArea = styled.div`
  display: flex;
  gap: 6px;

  &:hover ${OneProject}:not(:hover) ${ImgDiv},
  &:focus-within ${OneProject}:not(:focus-visible) ${ImgDiv} {
    filter: blur(2.5px);
    opacity: 0.68;
  }
`;

const Title = styled.div`
  display: flex;
  min-height: 18px;
  padding: 10px 2px;
  font-family: "raleway_regular";
  font-size: 15px;
  opacity: 1;
  transition: color 250ms ease;

  ${OneProject}:hover &,
  ${OneProject}:focus-visible & {
    color: var(--primary-cl);
  }
`;

const BreadCrumb = styled.div`
  padding: 30px 10px;
  width: 75%;
  font-family: "raleway_light";
  font-size: 15px;
`;

const ViewMore = styled.div`
  display: flex;
  width: 100%;
  margin: 10px 0;
  justify-content: flex-end;
  padding-right: 10px;

  p {
    display: flex;
    align-items: center;
    gap: 5px;
    font-family: "raleway_light";
    font-size: 13px;
    cursor: pointer;
  }

  svg {
    transition: transform 220ms ease;
  }

  p:hover svg {
    transform: translateX(5px);
  }
`;

const MobSlider = styled.div`
  display: flex !important;
  flex-direction: column;
`;

const Wrapper = styled.div`
  display: flex;
  height: 375px;
`;

const ProjectHighlight = styled.div`
  width: 65%;
`;

const SideBar = styled.div`
  width: 35%;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SmallPreview = styled.div`
  width: 83%;
  height: 30%;
  position: relative;
  cursor: pointer;

  ${(props) =>
    props.last &&
    `
    &::after {
      content: "";
      position: absolute;
      inset: 0;
      background: var(--primary-cl);
      opacity: 0.7;
    }
  `}

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  p {
    position: absolute;
    top: 13%;
    z-index: 1;
    color: #fff;
    font-size: 17px;
    text-align: center;
    cursor: pointer;
  }
`;

const ClientInfo = styled.div`
  p {
    text-align: center;
    font-size: 14px;
    font-family: "raleway_regular";
  }
`;
